import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Container } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router';

import {
  DuplicatePushBroadcastMutation,
  DuplicatePushBroadcastMutationVariables,
  GetPushBroadcastOptionsQuery,
  GetPushBroadcastsQuery,
  GetPushBroadcastsQueryVariables,
} from '../../../../../__gqltypes__';
import { LoadingLogo } from '../../../devtools/components/Modal';
import { PushBroadcastDetail } from './components/PushBroadcastDetail';
import { PushBroadcastList } from './components/PushBroadcastList';
import { PushIssueList } from './components/PushIssueList';
import { DUPLICATE_PUSH_BROADCAST, GET_PUSH_BROADCAST_OPTIONS, GET_PUSH_BROADCASTS } from './graphql';
import { usePushPeople } from './usePushPeople';
import { NEW_PUSH_BROADCAST_ID, PUSH_BROADCAST_BASE_URL, pushBroadcastErrorIssues, PushIssue } from './utils';

const LIST_LIMIT = 100;

function PushBroadcastIndex({ onCreate, onOpen }: { onCreate: () => void; onOpen: (id: string) => void }): JSX.Element {
  // Fetched each time the list is shown, so it reflects what was just sent or edited.
  const { data, loading, error } = useQuery<GetPushBroadcastsQuery, GetPushBroadcastsQueryVariables>(
    GET_PUSH_BROADCASTS,
    { variables: { limit: LIST_LIMIT }, fetchPolicy: 'network-only' }
  );
  const broadcasts = data?.pushBroadcasts ?? [];
  const people = usePushPeople(broadcasts.flatMap(({ createdBy, startedBy }) => [createdBy, startedBy]));
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [issues, setIssues] = useState<PushIssue[]>([]);
  const [duplicateBroadcast] = useMutation<DuplicatePushBroadcastMutation, DuplicatePushBroadcastMutationVariables>(
    DUPLICATE_PUSH_BROADCAST
  );
  const duplicate = async (id: string) => {
    setIssues([]);
    setDuplicatingId(id);
    try {
      const result = await duplicateBroadcast({ variables: { id } });
      if (result.data) {
        onOpen(result.data.duplicatePushBroadcast.id);
      }
    } catch (duplicateError) {
      setIssues(pushBroadcastErrorIssues(duplicateError));
    } finally {
      setDuplicatingId(null);
    }
  };
  return (
    <>
      <LoadingLogo show={loading && !data} />
      {error ? <Alert variant="danger">Failed to load the push notifications: {error.message}</Alert> : null}
      <PushIssueList issues={issues} title="Could not duplicate" />
      <PushBroadcastList
        broadcasts={broadcasts}
        people={people}
        onCreate={onCreate}
        onDuplicate={duplicate}
        duplicatingId={duplicatingId}
      />
      {broadcasts.length === LIST_LIMIT ? <small className="text-muted">Showing the newest {LIST_LIMIT}.</small> : null}
    </>
  );
}

/**
 * LiveOps push notifications: a push to a filtered audience, written in English with optional per-language
 * overrides. The list, then one broadcast's page (editor, preview, audience, send).
 */
export default function PushBroadcast(): JSX.Element {
  const { id: routeId } = useParams<{ id?: string }>();
  const history = useHistory();
  const selectedId = routeId && routeId !== ':id' ? routeId : null;

  // Languages, limits, categories and destinations: all from the backend, loaded once.
  const { data: options, loading, error } = useQuery<GetPushBroadcastOptionsQuery>(GET_PUSH_BROADCAST_OPTIONS);

  const open = (id: string | null) => history.push(id ? `${PUSH_BROADCAST_BASE_URL}/${id}` : PUSH_BROADCAST_BASE_URL);

  return (
    <Container fluid className="pb-4">
      <LoadingLogo show={loading && !options} />
      {error ? <Alert variant="danger">Failed to load the push notification options: {error.message}</Alert> : null}
      {options ? (
        selectedId ? (
          <PushBroadcastDetail
            key={selectedId}
            broadcastId={selectedId}
            options={options}
            onCreated={(created) => history.replace(`${PUSH_BROADCAST_BASE_URL}/${created.id}`)}
            onDeleted={() => open(null)}
            onDuplicated={(copy) => open(copy.id)}
          />
        ) : (
          <PushBroadcastIndex onCreate={() => open(NEW_PUSH_BROADCAST_ID)} onOpen={(id) => open(id)} />
        )
      ) : null}
    </Container>
  );
}
